import { useEffect, useRef, useState } from 'react';
import {
  setHours as setDateHours,
  setMinutes as setDateMinutes,
} from 'date-fns';
import { twMerge } from '@/utilities';

import { formatDate } from './utils';

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const dateToParts = (date: Date | undefined) => {
  if (!date) return { hours: '', minutes: '' };

  return {
    hours: String(date.getHours()).padStart(2, '0'),
    minutes: String(date.getMinutes()).padStart(2, '0'),
  };
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const digitsOnly = (value: string, maxLength = 2) =>
  value.replace(/\D/g, '').slice(0, maxLength);

type TimePickerProps = {
  label: string;
  value?: Date;
  onChange?: (value: Date) => void;
  disabled?: boolean;
  className?: string;
  error?: boolean;
};

export const TimePicker: React.FC<TimePickerProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  className,
  error = false,
}) => {
  const hoursRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef<HTMLInputElement>(null);
  const [hours, setHours] = useState(() => dateToParts(value).hours);
  const [minutes, setMinutes] = useState(() => dateToParts(value).minutes);

  const valueTime = value?.getTime();

  useEffect(() => {
    const parsed = dateToParts(value);
    setHours(parsed.hours);
    setMinutes(parsed.minutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueTime]);

  const emitChange = (nextHours: string, nextMinutes: string) => {
    if (!value || !nextHours || !nextMinutes) return;

    onChange?.(
      setDateMinutes(
        setDateHours(value, parseInt(nextHours, 10)),
        parseInt(nextMinutes, 10)
      )
    );
  };

  const normalizeHours = (raw: string) => {
    if (!raw) return '';
    const numeric = clamp(parseInt(raw, 10) || 0, 0, 23);
    return String(numeric).padStart(raw.length === 1 ? 1 : 2, '0');
  };

  const normalizeMinutes = (raw: string) => {
    if (!raw) return '';
    const numeric = clamp(parseInt(raw, 10) || 0, 0, 59);
    return String(numeric).padStart(raw.length === 1 ? 1 : 2, '0');
  };

  const handleHoursChange = (raw: string) => {
    const next = digitsOnly(raw);
    setHours(next);

    if (next.length === 2) {
      minutesRef.current?.focus();
      minutesRef.current?.select();
    }

    if (next.length === 2 && minutes.length === 2) {
      emitChange(next, minutes);
    }
  };

  const handleMinutesChange = (raw: string) => {
    const next = digitsOnly(raw);
    setMinutes(next);

    if (next.length === 2 && hours.length >= 1) {
      emitChange(hours, next);
    }
  };

  const handleHoursBlur = () => {
    if (!hours) return;

    const normalized = normalizeHours(hours);
    setHours(normalized);

    if (minutes) {
      emitChange(normalized, normalizeMinutes(minutes));
    }
  };

  const handleMinutesBlur = () => {
    if (!minutes) return;

    const normalized = normalizeMinutes(minutes);
    setMinutes(normalized);

    if (hours) {
      emitChange(normalizeHours(hours), normalized);
    }
  };

  const adjustValue = (part: 'hours' | 'minutes', delta: number) => {
    if (part === 'hours') {
      const current = hours ? parseInt(hours, 10) : 0;
      const next = String(clamp(current + delta, 0, 23)).padStart(2, '0');
      setHours(next);
      emitChange(next, minutes || '00');
      return;
    }

    const current = minutes ? parseInt(minutes, 10) : 0;
    const next = String(clamp(current + delta, 0, 59)).padStart(2, '0');
    setMinutes(next);
    emitChange(hours || '00', next);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    part: 'hours' | 'minutes'
  ) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      adjustValue(part, 1);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      adjustValue(part, -1);
      return;
    }

    if (part === 'minutes' && event.key === 'Backspace' && !minutes) {
      hoursRef.current?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').trim();
    if (!value || !TIME_RE.test(pasted)) return;

    event.preventDefault();
    const [, pastedHours, pastedMinutes] = pasted.match(TIME_RE) ?? [];
    if (!pastedHours || !pastedMinutes) return;

    setHours(pastedHours);
    setMinutes(pastedMinutes);
    onChange?.(
      setDateMinutes(
        setDateHours(value, parseInt(pastedHours, 10)),
        parseInt(pastedMinutes, 10)
      )
    );
    minutesRef.current?.focus();
    minutesRef.current?.select();
  };

  const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    if ((event.target as HTMLElement).closest('input')) return;

    hoursRef.current?.focus();
    hoursRef.current?.select();
  };

  const inputClassName = twMerge(
    'w-[2.25ch] bg-transparent text-medium text-text text-center tabular-nums placeholder:text-textLight/60 rounded-xs',
    !disabled && !error && 'focus:outline focus:outline-2 focus:outline-main',
    !disabled && error && 'focus:outline focus:outline-2 focus:outline-danger'
  );

  return (
    <div
      className={twMerge(
        'flex items-center gap-sm rounded-sm px-sm py-2 shadow-sm bg-content min-w-[16rem] border border-active transition-colors',
        disabled && 'opacity-50 pointer-events-none',
        error && 'border-danger',
        className
      )}
      onClick={handleContainerClick}
    >
      <span className="text-small text-textLight whitespace-nowrap">
        {label}
      </span>

      <div className="flex items-center ml-auto">
        <span className="text-medium text-text whitespace-nowrap mr-2">
          {formatDate(value)}
        </span>

        <div className="flex items-center">
          <input
            ref={hoursRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            value={hours}
            placeholder="00"
            aria-label={`${label} hours`}
            maxLength={2}
            onChange={event => handleHoursChange(event.target.value)}
            onBlur={handleHoursBlur}
            onFocus={event => event.target.select()}
            onKeyDown={event => handleKeyDown(event, 'hours')}
            onPaste={handlePaste}
            className={inputClassName}
          />

          <span className="text-medium text-textLight select-none">:</span>

          <input
            ref={minutesRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            value={minutes}
            placeholder="00"
            aria-label={`${label} minutes`}
            maxLength={2}
            onChange={event => handleMinutesChange(event.target.value)}
            onBlur={handleMinutesBlur}
            onFocus={event => event.target.select()}
            onKeyDown={event => handleKeyDown(event, 'minutes')}
            onPaste={handlePaste}
            className={inputClassName}
          />
        </div>
      </div>
    </div>
  );
};
